import { useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectSession } from '../model/selectors';
import {
  SessionType,
  isRunningLocal,
  builtEnvironment,
  resolveOnboardingDestination,
} from '@shared';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { useFullPageRedirect } from '@client/hooks/use-full-page-redirect';
import { ClientLinkAdvisorBlock } from '@client/components/ClientLinkAdvisorBlock';
import { timeoutSignal } from '@client/util/timeoutSignal';
import { rememberSupportChatWithheld } from '@client/util/intercomUtil';

const MAX_RETRIES = 4;
const RETRY_BASE_DELAY_MS = 1000;

const FETCH_TIMEOUT_MS = 16000;

function buildClientSignInFallbackUrl(formReferenceId: string, sourceQuery: string): string {
  const redirectTo = `/onboarding-form/${formReferenceId}${sourceQuery}`;
  const params = new URLSearchParams({
    client_sign_in: 'true',
    redirect_to: redirectTo,
  });
  return `/?${params.toString()}`;
}

function jitteredDelay(attempt: number): Promise<void> {
  const cap = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
  const delay = Math.random() * cap;
  return new Promise(r => setTimeout(r, delay));
}

// A dropped connection can surface as a rejected fetch while the browser keeps
// reusing the same pooled socket on retry. A unique param per attempt plus
// no-store makes each retry a distinct, uncached request.
function withCacheBuster(url: string, attempt: number): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_retry=${attempt}-${Date.now()}`;
}

async function fetchWithRetry(url: string, retries: number): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(withCacheBuster(url, attempt), {
        signal: timeoutSignal(FETCH_TIMEOUT_MS),
        cache: 'no-store',
      });
      if (response.ok) {
        return response;
      }
      if (attempt < retries && response.status >= 500) {
        await jitteredDelay(attempt);
        continue;
      }
      return response;
    } catch (error) {
      if (attempt < retries) {
        await jitteredDelay(attempt);
        continue;
      }
      throw error;
    }
  }
  throw new Error('Exhausted retries');
}

export function OnboardingRedirect() {
  const { formReferenceId } = useParams<{ formReferenceId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Preserve the channel tag through every onward hop so the open-time resolver can record it.
  const source = searchParams.get('source');
  const sourceQuery = source ? `?source=${encodeURIComponent(source)}` : '';
  const { isClerkSignedIn: isAuthenticated, isClerkLoading: authLoading } = useClerkClientAuth();
  const session = useSelector(selectSession);
  // A ref, not state: SessionLoader closes the session in the same effect flush that starts the
  // redirect, and the urgent re-render that follows skips a pending state update. The effect would
  // then run again with the flag still false and request the destination twice.
  const hasRedirectedRef = useRef(false);
  const fullPageRedirect = useFullPageRedirect();
  const isAdvisorViewingLink =
    !authLoading &&
    isAuthenticated &&
    (session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard);

  useEffect(() => {
    if (!formReferenceId || hasRedirectedRef.current) {
      return;
    }

    if (authLoading) {
      return;
    }

    // This is a client-specific share link. An advisor must never resolve it into the provider
    // form; their authenticated fill-form workflow enters through /onboarding-form directly.
    if (isAdvisorViewingLink) {
      return;
    }

    if (isAuthenticated && session.sessionType === SessionType.unknown) {
      return;
    }

    if (isAuthenticated && session.sessionType === SessionType.client) {
      hasRedirectedRef.current = true;
      navigate(`/onboarding-form/${formReferenceId}${sourceQuery}`, { replace: true });
      return;
    }

    if (!isAuthenticated) {
      hasRedirectedRef.current = true;

      // The lookup failed, so the client's program is unknown. Chat stays closed on the sign-in
      // page until signing in tells the app who they are.
      function fallBackToClientSignIn() {
        rememberSupportChatWithheld(true);
        navigate(buildClientSignInFallbackUrl(formReferenceId!, sourceQuery), { replace: true });
      }

      async function handleRedirect() {
        try {
          const env = isRunningLocal() ? 'dev' : builtEnvironment;
          const response = await fetchWithRetry(
            `/${env}/api/onboarding/${formReferenceId}${sourceQuery}`,
            MAX_RETRIES,
          );

          if (!response.ok) {
            console.warn(
              'Onboarding redirect API returned non-OK status, falling back to client sign-in flow',
            );
            fallBackToClientSignIn();
            return;
          }

          const data = await response.json();
          // The sign-in page this hands over to cannot ask the server who the visitor is yet. A
          // response without the answer is treated as withheld.
          rememberSupportChatWithheld(data.supportChatWithheld !== false);
          const destination = resolveOnboardingDestination(data);
          fullPageRedirect(destination.url);
        } catch (error) {
          console.error('Error fetching onboarding redirect after retries:', error);
          fallBackToClientSignIn();
        }
      }

      handleRedirect();
    }
  }, [
    formReferenceId,
    sourceQuery,
    navigate,
    isAuthenticated,
    authLoading,
    session.sessionType,
    isAdvisorViewingLink,
    fullPageRedirect,
  ]);

  if (isAdvisorViewingLink) {
    return <ClientLinkAdvisorBlock advisorId={session.advisor?.advisorId} />;
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-purple-600"></div>
      </div>
    </div>
  );
}
